// BillingAndRewardsPage.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { User, Ticket, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProfileSubscription } from "@/components/ProfileAndSubscription";
import { RedeemCodes } from "@/components/RedeemCode";
import { ReferralNetwork } from "@/components/ReferralNetwork";
import billingAndRewardsApi from "@/services/billingAndRewards";
import type { ProfileSubscriptionResponse, ReferralLogItem } from "@/types/BillingAndRewards.dto.types";

export const BillingAndRewardsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState(0);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [userProfile, setUserProfile] = useState<ProfileSubscriptionResponse | undefined>(undefined);
  const [referralHistory, setReferralHistory] = useState<ReferralLogItem[]>([]);

  useEffect(() => {
    billingAndRewardsApi.getSubscriptionProfile().then((data) => {
      setUserProfile(data);
    });
    billingAndRewardsApi.getReferralHistory().then((data) => {
      setReferralHistory(data);
    });
  }, []);

  // Helper for computing avatar initials cleanly
  const initials = React.useMemo(() => {
    const firstInitial = userProfile?.firstName?.[0] ?? "";
    const lastInitial = userProfile?.lastName?.[0] ?? "";
    return (firstInitial + lastInitial).toUpperCase() || "U";
  }, [userProfile]);

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        pt: { xs: 4, md: 8 },
        pb: 12,
      }}
    >
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 1,
            }}
          >
            {t("pages.billingAndRewards.title")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("pages.billingAndRewards.subtitle")}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Side Navigation Matrix */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <CardContent sx={{ p: 3, textAlign: "center" }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    mb: 2,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "1.25rem",
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                  {userProfile ? `${userProfile.firstName ?? ""} ${userProfile.lastName ?? ""}`.trim() : "..."}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                  {userProfile?.email ?? "-"}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Tabs
                  orientation="vertical"
                  value={activeTab}
                  onChange={(_, newValue) => {
                    setActiveTab(newValue);
                    setFeedback(null);
                  }}
                  sx={{
                    alignItems: "stretch",
                    "& .MuiTab-root": {
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "left",
                      borderRadius: 1,
                      py: 1.5,
                      px: 2,
                      minHeight: "auto",
                      mb: 0.5,
                      color: "text.secondary",
                      textTransform: "none",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: "primary.main",
                      },
                    },
                    "& .Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                    },
                    "& .MuiTabs-indicator": {
                      left: 0,
                      right: "auto",
                      width: 3,
                      borderRadius: 1.5,
                      bgcolor: "primary.main",
                    },
                  }}
                >
                  <Tab
                    icon={<User size={18} style={{ marginRight: 10 }} />}
                    iconPosition="start"
                    label={t("pages.billingAndRewards.tabs.profileAndSubscription")}
                  />
                  <Tab
                    icon={<Ticket size={18} style={{ marginRight: 10 }} />}
                    iconPosition="start"
                    label={t("pages.billingAndRewards.tabs.redeemCodes")}
                  />
                  <Tab
                    icon={<Share2 size={18} style={{ marginRight: 10 }} />}
                    iconPosition="start"
                    label={t("pages.billingAndRewards.tabs.referralNetwork")}
                  />
                </Tabs>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side Content Viewport */}
          <Grid size={{ xs: 12, md: 9 }}>
            {feedback && (
              <Alert severity={feedback.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            {/* TAB 1: PROFILE, PLANS, AND PRICING */}
            {activeTab === 0 && userProfile && <ProfileSubscription data={userProfile} />}

            {/* TAB 2: PROMO CODE & REFERRAL REDEEM MODAL COMPONENTS */}
            {activeTab === 1 && <RedeemCodes onFeedback={(_feedback) => setFeedback(_feedback)} />}

            {/* TAB 3: REFERRAL HISTORY LEDGER */}
            {activeTab === 2 && userProfile && (
              <ReferralNetwork referralCode={userProfile.referralCode} history={referralHistory} />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
